/**
 * AI prediction service: the per-component "AI Prediction" column. Builds
 * the prompt (the seven critical analysis rules, readings with up to 15
 * history points newest first, calculated trends, lifecycle data,
 * dependencies, spare parts), parses and sanity-checks the model's JSON, and
 * falls back to the MTBF model when there are no sensors, on the 45 s local
 * timeout, on an API error or on a parse error. Port of
 * ai-prediction-service.js; the transport, the lifecycle knowledge, the
 * inventory, time and timers are injected.
 */
import type { DataItem, HistoryPoint } from '../../types';
import { COMPONENT_LIFETIMES } from '../constants';
import { addDaysMs } from '../componentPrediction';
import type { InventoryManager } from '../inventory';
import { buildAiMessages, type AiTransport } from './transport';

export interface FailureMode {
  mode: string;
  probability: number;
  severity: string;
  leadIndicators?: { sensor: string; behavior: string; leadTimeDays: number }[];
  consequence?: string;
}

export interface EquipmentLifecycle {
  name?: string;
  category?: string;
  manufacturer?: string;
  mtbf?: { hours?: number; years: number; confidenceLevel?: number; source?: string };
  expectedLifespan?: { normalYears: number; extendedYears?: number; factors?: string[] };
  failureModes?: FailureMode[];
  [key: string]: unknown;
}

export interface ComponentDependency {
  dependsOn?: string[];
  dependents?: string[];
  impactIfFailed?: unknown;
}

/** Shape of public/data/knowledge/component-lifecycle.json (the parts the service reads). */
export interface LifecycleKnowledge {
  equipmentTypes: Record<string, EquipmentLifecycle>;
  componentDependencies: Record<string, ComponentDependency>;
  [key: string]: unknown;
}

/** The knowledge used when the JSON fails to load. */
export function getDefaultLifecycleKnowledge(): LifecycleKnowledge {
  return {
    equipmentTypes: {
      cooling_fan: { mtbf: { years: 5 }, expectedLifespan: { normalYears: 5 } },
      lna: { mtbf: { years: 10 }, expectedLifespan: { normalYears: 10 } },
      hpa: { mtbf: { years: 7 }, expectedLifespan: { normalYears: 7 } },
      antenna_drive: { mtbf: { years: 15 }, expectedLifespan: { normalYears: 15 } },
      modem: { mtbf: { years: 10 }, expectedLifespan: { normalYears: 10 } },
      network_switch: { mtbf: { years: 7 }, expectedLifespan: { normalYears: 7 } },
    },
    componentDependencies: {},
  };
}

/** The component fields the service reads (a system report row qualifies). */
export interface PredictionComponent {
  id: string;
  name: string;
  category?: string;
  status?: string;
  statusColour?: string;
  health?: number;
  healthTrend?: number;
  data?: DataItem[];
}

export interface SensorReading {
  id: string;
  name: string;
  value: unknown;
  unit: string;
  status: string;
  normalRange?: { low?: number; high?: number; min?: number; max?: number } | undefined;
  history: HistoryPoint[];
}

export interface SensorTrend {
  sensorId: string;
  sensorName: string;
  direction: 'rising' | 'falling' | 'stable';
  slope: number;
  rateOfChange: number;
  currentValue: number;
  avgValue: number;
  minValue: number;
  maxValue: number;
  variance: number;
  normalRange: SensorReading['normalRange'];
  isAnomalous: boolean;
}

export interface InventoryInfo {
  partNumber: string;
  name: string;
  quantity: number;
  minStock: number;
  leadTimeDays: number;
  status: 'OUT_OF_STOCK' | 'LOW_STOCK' | 'IN_STOCK';
}

export interface AnalysisData {
  component: {
    id: string;
    name: string;
    category: string | undefined;
    status: string | undefined;
    statusColour: string | undefined;
    health: number | undefined;
    healthTrend: number;
  };
  sensorReadings: SensorReading[];
  historicalTrends: SensorTrend[];
  lifecycleInfo: { type: string; data: EquipmentLifecycle } | null;
  dependencies: string[];
  dependents: string[];
  impactIfFailed?: unknown;
  inventory: InventoryInfo | null;
}

export interface AiLeadIndicator {
  sensor: string;
  concern?: string;
  trend?: string;
  rate?: string;
}

export interface AiPrediction {
  componentId: string;
  componentName: string;
  /** YYYY-MM-DD or null. */
  predictedFailureDate: string | null;
  daysUntilFailure: number | null;
  confidence: number;
  riskLevel: string;
  healthScore: number;
  aiHealth: number;
  failureMode: string | null;
  justification: string;
  leadIndicators: AiLeadIndicator[];
  recommendedAction: string;
  maintenanceWindow: string;
  inventoryAlert: string | null;
  dependencyRisk: string | null;
  trend: { direction: string; anomalousReadings: number };
  isAIGenerated: boolean;
  generatedAt: string;
}

export const AI_PREDICTION_SYSTEM_PROMPT = 'You are a predictive maintenance AI. Always respond with valid JSON only, no markdown.';
export const AI_PREDICTION_LOCAL_TIMEOUT_MS = 45_000;
export const AI_PREDICTION_BATCH_CONCURRENCY = 3;

export interface AiPredictionDeps {
  transport: AiTransport;
  lifecycle: LifecycleKnowledge | null;
  inventory?: Pick<InventoryManager, 'getInventoryForEquipment'> | null;
  nowMs: () => number;
  timeoutMs?: number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export interface GenerateOptions {
  includeInventory?: boolean;
  forceRefresh?: boolean;
}

export type BatchProgress = (componentId: string, prediction: AiPrediction, completed: number, total: number) => void;

export interface AiPredictionService {
  /** Never rejects: every failure path resolves with the MTBF fallback. */
  generatePrediction(component: PredictionComponent, options?: GenerateOptions): Promise<AiPrediction>;
  /** Three at a time, progress per completion, keyed by component id. */
  generateBatchPredictions(
    components: PredictionComponent[],
    options?: GenerateOptions,
    onProgress?: BatchProgress,
  ): Promise<Record<string, AiPrediction>>;
  collectAnalysisData(component: PredictionComponent, options?: GenerateOptions): AnalysisData;
  getLifecycleKnowledge(equipmentType: string): EquipmentLifecycle | null;
  getAllDependencies(): Record<string, ComponentDependency>;
}

function parseHistoryNumbers(history: HistoryPoint[]): number[] {
  return history.map((h) => parseFloat(String(h.value).replace(/[^0-9.-]/g, ''))).filter((v) => !isNaN(v));
}

export function isAnomalous(value: number, normalRange: SensorReading['normalRange']): boolean {
  if (!normalRange) return false;
  const min = normalRange.low !== undefined ? normalRange.low : normalRange.min;
  const max = normalRange.high !== undefined ? normalRange.high : normalRange.max;
  return value < (min as number) || value > (max as number);
}

/** Per item with at least three numeric history points (first 20) and a normalRange: slope over the newest-first values (±0.01 direction), stats, and anomaly against the range. */
export function calculateTrends(sensorData: DataItem[]): SensorTrend[] {
  const trends: SensorTrend[] = [];
  for (const sensor of sensorData) {
    if (!sensor.history || sensor.history.length < 3) continue;
    if (!sensor.normalRange) continue;
    const values = parseHistoryNumbers(sensor.history.slice(0, 20));
    if (values.length < 3) continue;

    const n = values.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumXX += i * i;
    }
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const avgValue = sumY / n;
    const direction: SensorTrend['direction'] = slope > 0.01 ? 'rising' : slope < -0.01 ? 'falling' : 'stable';

    let variance = 0;
    let minValue = values[0];
    let maxValue = values[0];
    for (const v of values) {
      variance += Math.pow(v - avgValue, 2);
      if (v < minValue) minValue = v;
      if (v > maxValue) maxValue = v;
    }
    variance /= n;

    trends.push({
      sensorId: sensor.id,
      sensorName: sensor.label || sensor.id,
      direction,
      slope,
      rateOfChange: values.length >= 2 ? (values[0] - values[values.length - 1]) / values.length : 0,
      currentValue: values[0],
      avgValue,
      minValue,
      maxValue,
      variance,
      normalRange: sensor.normalRange,
      isAnomalous: isAnomalous(values[0], sensor.normalRange),
    });
  }
  return trends;
}

const CATEGORY_TYPES: Record<string, string[]> = {
  'RF Equipment': ['lna', 'hpa', 'freq_translator', 'waveguide'],
  'Thermal Management': ['cooling_fan'],
  Mechanical: ['antenna_drive'],
  'Digital Equipment': ['modem', 'network_switch'],
};

/** The first equipment type contained in the id (knowledge order), else the category's first type. */
export function getLifecycleInfo(
  componentId: string,
  category: string | undefined,
  knowledge: LifecycleKnowledge | null,
): { type: string; data: EquipmentLifecycle } | null {
  if (!knowledge || !knowledge.equipmentTypes) return null;
  const id = componentId.toLowerCase();
  const equipTypes = knowledge.equipmentTypes;
  for (const type of Object.keys(equipTypes)) {
    if (id.indexOf(type) !== -1) return { type, data: equipTypes[type] };
  }
  const categoryTypes = category ? CATEGORY_TYPES[category] : undefined;
  if (categoryTypes && categoryTypes.length > 0) {
    const fallbackType = categoryTypes[0];
    if (equipTypes[fallbackType]) return { type: fallbackType, data: equipTypes[fallbackType] };
  }
  return null;
}

export function getDependencies(
  componentId: string,
  knowledge: LifecycleKnowledge | null,
): { dependencies: string[]; dependents: string[]; impactIfFailed?: unknown } {
  const result: { dependencies: string[]; dependents: string[]; impactIfFailed?: unknown } = { dependencies: [], dependents: [] };
  if (!knowledge || !knowledge.componentDependencies) return result;
  const id = componentId.toLowerCase();
  for (const comp of Object.keys(knowledge.componentDependencies)) {
    const entry = knowledge.componentDependencies[comp];
    if (id.indexOf(comp) !== -1 && entry) {
      result.dependencies = entry.dependsOn || [];
      result.dependents = entry.dependents || [];
      result.impactIfFailed = entry.impactIfFailed;
      break;
    }
  }
  return result;
}

/** The primary spare looked up by the component id as an equipment key (matches only ids that are equipment keys). */
export function getInventoryInfo(
  componentId: string,
  inventory: Pick<InventoryManager, 'getInventoryForEquipment'> | null | undefined,
): InventoryInfo | null {
  if (!inventory) return null;
  const status = inventory.getInventoryForEquipment(componentId);
  if (!status) return null;
  return {
    partNumber: status.partNumber,
    name: status.name,
    quantity: status.quantity,
    minStock: status.minStock,
    leadTimeDays: status.leadTimeDays,
    status: status.quantity === 0 ? 'OUT_OF_STOCK' : status.quantity <= status.minStock ? 'LOW_STOCK' : 'IN_STOCK',
  };
}

export function collectAnalysisData(
  component: PredictionComponent,
  options: GenerateOptions,
  knowledge: LifecycleKnowledge | null,
  inventory: Pick<InventoryManager, 'getInventoryForEquipment'> | null | undefined,
): AnalysisData {
  const data: AnalysisData = {
    component: {
      id: component.id,
      name: component.name,
      category: component.category,
      status: component.status,
      statusColour: component.statusColour,
      health: component.health,
      healthTrend: component.healthTrend || 0,
    },
    sensorReadings: [],
    historicalTrends: [],
    lifecycleInfo: null,
    dependencies: [],
    dependents: [],
    inventory: null,
  };
  if (component.data) {
    data.sensorReadings = component.data.map((d) => ({
      id: d.id,
      name: d.label || d.id,
      value: d.value,
      unit: '',
      status: d.status || 'green',
      normalRange: d.normalRange,
      history: d.history ? d.history.slice(0, 20) : [],
    }));
    data.historicalTrends = calculateTrends(component.data);
  }
  data.lifecycleInfo = getLifecycleInfo(component.id, component.category, knowledge);
  const deps = getDependencies(component.id, knowledge);
  data.dependencies = deps.dependencies;
  data.dependents = deps.dependents;
  data.impactIfFailed = deps.impactIfFailed;
  if (options.includeInventory !== false) data.inventory = getInventoryInfo(component.id, inventory);
  return data;
}

function historyLabel(point: HistoryPoint, idx: number): string {
  const timestamp = point.datetime || '';
  if (timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return 'T-' + idx;
}

/** The user prompt, verbatim from the original except that a range low of 0 prints 0 (the original's `low || min` printed "undefined"). */
export function buildPredictionPrompt(data: AnalysisData): string {
  let prompt = `You are an expert predictive maintenance AI analyst for satellite ground station equipment.
Analyze the following component data and provide a failure prediction with detailed justification.

CRITICAL ANALYSIS RULES:
1. HISTORICAL DATA is the PRIMARY indicator - examine the trend direction and rate of change
2. If ANY sensor shows [YELLOW] or [RED] status, this is ALREADY a warning condition
3. For COOLING FANS specifically:
   - RPM declining + Temperature rising = BEARING FAILURE pattern
   - If RPM is below normal range OR declining trend visible: predict failure within 30-60 days
   - If RPM is critically low (<80% of normal min) AND temperature elevated: predict failure within 7-30 days
4. Calculate days until failure by:
   - Measuring the degradation rate from historical readings
   - Extrapolating when values will exceed critical thresholds
   - If degradation is accelerating, use shorter estimates
5. Health below 70% with declining trend = HIGH risk, predict failure within 90 days
6. Health below 50% = CRITICAL risk, predict failure within 30 days
7. If current readings are already outside normal range, failure is IMMINENT (7-30 days)

COMPONENT: ${data.component.name} (${data.component.id})
Category: ${data.component.category}
Current Status: ${data.component.status} (${data.component.statusColour})
Current Health: ${data.component.health}%
Health Trend: ${data.component.healthTrend > 0 ? 'Declining' : data.component.healthTrend < 0 ? 'Improving' : 'Stable'}

`;

  if (data.sensorReadings.length > 0) {
    prompt += 'CURRENT SENSOR READINGS:\n';
    for (const s of data.sensorReadings) {
      const rangeStr = s.normalRange
        ? ' (Normal: ' + (s.normalRange.low ?? s.normalRange.min) + '-' + (s.normalRange.high ?? s.normalRange.max) + ')'
        : '';
      prompt += '- ' + s.name + ': ' + s.value + ' ' + s.unit + ' [' + s.status.toUpperCase() + ']' + rangeStr + '\n';
      if (s.history && s.history.length > 0) {
        const historyStr = s.history
          .slice(0, 15)
          .map((h, idx) => historyLabel(h, idx) + ':' + h.value)
          .join(' → ');
        prompt += '  Historical readings (newest→oldest): ' + historyStr + '\n';
      }
    }
    prompt += '\n';
  }

  if (data.historicalTrends.length > 0) {
    prompt += 'CALCULATED TREND ANALYSIS:\n';
    for (const t of data.historicalTrends) {
      prompt += '- ' + t.sensorName + ': ' + t.direction.toUpperCase();
      if (t.isAnomalous) prompt += ' [ANOMALOUS]';
      prompt += ' (current: ' + t.currentValue.toFixed(2) + ', avg: ' + t.avgValue.toFixed(2);
      if (t.minValue !== undefined && t.maxValue !== undefined) {
        prompt += ', min: ' + t.minValue.toFixed(2) + ', max: ' + t.maxValue.toFixed(2);
      }
      prompt += ')\n';
    }
    prompt += '\n';
  }

  if (data.lifecycleInfo) {
    const lc = data.lifecycleInfo.data;
    prompt += 'LIFECYCLE DATA:\n';
    prompt += '- Equipment Type: ' + data.lifecycleInfo.type + '\n';
    if (lc.mtbf) prompt += '- MTBF: ' + lc.mtbf.years + ' years (confidence: ' + lc.mtbf.confidenceLevel + '%)\n';
    if (lc.expectedLifespan) {
      prompt += '- Expected Lifespan: ' + lc.expectedLifespan.normalYears + '-' + lc.expectedLifespan.extendedYears + ' years\n';
    }
    if (lc.failureModes) {
      prompt += '- Known Failure Modes:\n';
      for (const fm of lc.failureModes) {
        prompt += '  * ' + fm.mode + ' (probability: ' + fm.probability * 100 + '%, severity: ' + fm.severity + ')\n';
      }
    }
    prompt += '\n';
  }

  if (data.dependencies.length > 0 || data.dependents.length > 0) {
    prompt += 'DEPENDENCIES:\n';
    if (data.dependencies.length > 0) prompt += '- Depends on: ' + data.dependencies.join(', ') + '\n';
    if (data.dependents.length > 0) prompt += '- Systems depending on this: ' + data.dependents.join(', ') + '\n';
    prompt += '\n';
  }

  if (data.inventory) {
    prompt += 'SPARE PARTS INVENTORY:\n';
    prompt += '- Part: ' + data.inventory.name + '\n';
    prompt += '- In Stock: ' + data.inventory.quantity + ' units (' + data.inventory.status + ')\n';
    prompt += '- Lead Time: ' + data.inventory.leadTimeDays + ' days\n\n';
  }

  prompt += `Based on all available data, provide your prediction in the following JSON format:
{
    "predictedFailureDate": "YYYY-MM-DD or null if no failure predicted within 2 years",
    "daysUntilFailure": number or null,
    "confidence": 0-100,
    "riskLevel": "LOW|MEDIUM|HIGH|CRITICAL",
    "healthScore": 0-100,
    "failureMode": "Most likely failure mode if applicable",
    "justification": "2-3 sentence detailed explanation of why this prediction was made, citing specific data points",
    "leadIndicators": [
        {"sensor": "name", "concern": "description of concerning trend"}
    ],
    "recommendedAction": "Specific actionable recommendation",
    "maintenanceWindow": "Recommended timeframe for maintenance",
    "inventoryAlert": "Alert if spare parts are needed (or null)",
    "dependencyRisk": "Impact on dependent systems if this fails"
}

If no failure is predicted within 2 years, set predictedFailureDate to null and provide estimated remaining lifespan based on industry MTBF data.`;

  return prompt;
}

/** The newest-first JSON object in a string response, or the object itself; null when neither parses. */
export function extractPredictionJson(response: unknown): Record<string, unknown> | null {
  if (response && typeof response === 'object') return response as Record<string, unknown>;
  if (typeof response !== 'string') return null;
  const jsonMatch = response.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  try {
    return JSON.parse(jsonMatch[0]) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function determineTrend(analysisData: AnalysisData): { direction: string; anomalousReadings: number } {
  let dominantTrend = 'stable';
  let anomalousCount = 0;
  for (const t of analysisData.historicalTrends) {
    if (t.isAnomalous) anomalousCount++;
    if (t.direction !== 'stable') dominantTrend = t.direction;
  }
  return { direction: dominantTrend, anomalousReadings: anomalousCount };
}

function isoDate(ms: number): string {
  return new Date(ms).toISOString().split('T')[0];
}

/**
 * The model's JSON with the sanity overrides: by health (≤20 CRITICAL within
 * 7 or 30 days, ≤40 at least HIGH within 60 days, ≤60 lifts LOW to MEDIUM,
 * ≥90 cancels a failure within 30 days, ≥80 one within 60 days) and by
 * sensor colours (any red: CRITICAL within 14 days; two yellow: HIGH within
 * 45; one yellow or two declining trends: MEDIUM within 90). Falls back to
 * the MTBF model when the response holds no JSON.
 */
export function parseAIPrediction(response: unknown, analysisData: AnalysisData, nowMs: number): AiPrediction {
  const parsed = extractPredictionJson(response);
  if (!parsed) return generateFallbackPrediction(analysisData.component, nowMs);

  const componentHealth = analysisData.component.health || 100;
  const name = analysisData.component.name;

  let daysUntilFailure = parsed.daysUntilFailure as number | null | undefined;
  let predictedFailureDate = parsed.predictedFailureDate as string | null | undefined;
  let riskLevel = (parsed.riskLevel as string) || 'MEDIUM';
  let recommendedAction = (parsed.recommendedAction as string) || 'Continue monitoring';
  let maintenanceWindow = (parsed.maintenanceWindow as string) || 'Standard schedule';
  let failureMode = (parsed.failureMode as string) || null;
  let inventoryAlert = (parsed.inventoryAlert as string) || null;
  let leadIndicators = (parsed.leadIndicators as AiLeadIndicator[]) || [];
  const isRoutine = (action: string): boolean =>
    action.indexOf('routine monitoring') !== -1 || action.indexOf('No immediate action') !== -1;

  if (componentHealth <= 20) {
    riskLevel = 'CRITICAL';
    if (!daysUntilFailure || daysUntilFailure > 30) {
      daysUntilFailure = componentHealth <= 10 ? 7 : 30;
      predictedFailureDate = isoDate(addDaysMs(nowMs, daysUntilFailure));
    }
    recommendedAction = 'IMMEDIATE ACTION REQUIRED. Component is critically degraded. Replace or repair immediately.';
    maintenanceWindow = 'Immediate';
    if (!inventoryAlert) inventoryAlert = 'Verify spare parts availability for emergency replacement.';
  } else if (componentHealth <= 40) {
    if (riskLevel === 'LOW' || riskLevel === 'MEDIUM') riskLevel = 'HIGH';
    if (!daysUntilFailure || daysUntilFailure > 90) {
      daysUntilFailure = 60;
      predictedFailureDate = isoDate(addDaysMs(nowMs, daysUntilFailure));
    }
    if (isRoutine(recommendedAction)) {
      recommendedAction = 'Schedule replacement within 30-60 days. Monitor closely for further degradation.';
    }
    maintenanceWindow = 'Within 30 days';
  } else if (componentHealth <= 60 && riskLevel === 'LOW') {
    riskLevel = 'MEDIUM';
    if (isRoutine(recommendedAction)) recommendedAction = 'Schedule inspection. Component showing degradation.';
    maintenanceWindow = 'Within 2-3 months';
  } else if (componentHealth >= 90 && daysUntilFailure !== null && daysUntilFailure !== undefined && daysUntilFailure <= 30) {
    daysUntilFailure = null;
    predictedFailureDate = null;
    riskLevel = 'LOW';
    recommendedAction = 'Continue routine monitoring. No immediate action required.';
    maintenanceWindow = 'Standard schedule';
    failureMode = null;
    inventoryAlert = null;
    leadIndicators = [];
  } else if (componentHealth >= 80 && daysUntilFailure !== null && daysUntilFailure !== undefined && daysUntilFailure <= 60) {
    daysUntilFailure = null;
    predictedFailureDate = null;
    riskLevel = 'LOW';
    recommendedAction = 'Continue monitoring. Component is healthy.';
    maintenanceWindow = 'Standard schedule';
    inventoryAlert = null;
    leadIndicators = [];
  }

  let yellowSensors = 0;
  let redSensors = 0;
  let decliningTrends = 0;
  for (const s of analysisData.sensorReadings) {
    if (s.status === 'yellow') yellowSensors++;
    if (s.status === 'red') redSensors++;
  }
  const id = analysisData.component.id.toLowerCase();
  for (const t of analysisData.historicalTrends) {
    if (id.indexOf('fan') !== -1) {
      const sensorName = t.sensorName.toLowerCase();
      if ((sensorName.indexOf('rpm') !== -1 || sensorName.indexOf('speed') !== -1) && t.direction === 'falling') decliningTrends++;
      if (sensorName.indexOf('temp') !== -1 && t.direction === 'rising') decliningTrends++;
    }
    if (t.isAnomalous) decliningTrends++;
  }

  if (redSensors > 0 && (riskLevel === 'LOW' || riskLevel === 'MEDIUM')) {
    riskLevel = 'CRITICAL';
    if (!daysUntilFailure || daysUntilFailure > 30) {
      daysUntilFailure = 14;
      predictedFailureDate = isoDate(addDaysMs(nowMs, daysUntilFailure));
    }
    recommendedAction = 'URGENT: Sensor readings are critical. Schedule immediate replacement.';
    maintenanceWindow = 'Immediate';
  } else if (yellowSensors >= 2 && riskLevel === 'LOW') {
    riskLevel = 'HIGH';
    if (!daysUntilFailure || daysUntilFailure > 60) {
      daysUntilFailure = 45;
      predictedFailureDate = isoDate(addDaysMs(nowMs, daysUntilFailure));
    }
    recommendedAction = 'Multiple sensors showing warning levels. Schedule replacement within 30 days.';
    maintenanceWindow = 'Within 30 days';
  } else if ((yellowSensors >= 1 || decliningTrends >= 2) && riskLevel === 'LOW') {
    riskLevel = 'MEDIUM';
    if (!daysUntilFailure || daysUntilFailure > 120) {
      daysUntilFailure = 90;
      predictedFailureDate = isoDate(addDaysMs(nowMs, daysUntilFailure));
    }
    recommendedAction = 'Degradation trend detected. Schedule inspection within 60 days.';
    maintenanceWindow = 'Within 60 days';
  }

  let justification = parsed.justification as string | undefined;
  if (!justification || justification === 'AI analysis completed.' || justification.length < 20) {
    const health = (parsed.healthScore as number) || componentHealth;
    if (predictedFailureDate && daysUntilFailure) {
      justification = name + ' shows ' + riskLevel.toLowerCase() + ' risk based on current sensor trends. ';
      if (failureMode) justification += 'Primary concern: ' + failureMode + '. ';
      justification +=
        'Predicted maintenance need by ' + predictedFailureDate + ' with ' + ((parsed.confidence as number) || 70) + '% confidence.';
    } else {
      justification = name + ' is operating within acceptable parameters at ' + health + '% health. ';
      justification += 'No failure predicted within 2 years based on current readings and historical MTBF data.';
    }
  }

  return {
    componentId: analysisData.component.id,
    componentName: name,
    predictedFailureDate: predictedFailureDate ?? null,
    daysUntilFailure: daysUntilFailure ?? null,
    confidence: (parsed.confidence as number) || 70,
    riskLevel,
    healthScore: (parsed.healthScore as number) || componentHealth,
    aiHealth: (parsed.healthScore as number) || componentHealth,
    failureMode,
    justification,
    leadIndicators,
    recommendedAction,
    maintenanceWindow,
    inventoryAlert,
    dependencyRisk: (parsed.dependencyRisk as string) || null,
    trend: determineTrend(analysisData),
    isAIGenerated: true,
    generatedAt: new Date(nowMs).toISOString(),
  };
}

/**
 * The MTBF fallback: remaining days = base lifetime (COMPONENT_LIFETIMES by
 * id or name keyword, else default) × health, ×0.7 for fans, motors and
 * drives; risk CRITICAL below 40 % health or 60 days, HIGH below 60 % or
 * 180 days, MEDIUM below 80 % or 365 days, else LOW (no date beyond 730 days).
 */
export function generateFallbackPrediction(component: PredictionComponent | AnalysisData['component'], nowMs: number): AiPrediction {
  const health = component.health || 100;
  let riskLevel: string;
  let daysUntilFailure: number | null;
  let predictedDate: string | null = null;
  let failureMode: string | null = null;
  let justification: string;
  let recommendedAction: string;
  let maintenanceWindow: string;
  const leadIndicators: AiLeadIndicator[] = [];

  const componentId = (component.id || '').toLowerCase();
  const componentName = component.name || component.id || 'Unknown';
  const category = component.category || 'Other';

  let mtbfData: (typeof COMPONENT_LIFETIMES)[string] | null = null;
  let baseLifetimeYears = 10;
  for (const type of Object.keys(COMPONENT_LIFETIMES)) {
    if (componentId.indexOf(type) !== -1 || componentName.toLowerCase().indexOf(type) !== -1) {
      mtbfData = COMPONENT_LIFETIMES[type];
      baseLifetimeYears = mtbfData.baseLifetime;
      break;
    }
  }
  if (!mtbfData) {
    mtbfData = COMPONENT_LIFETIMES.default;
    baseLifetimeYears = mtbfData.baseLifetime;
  }

  const remainingLifeFraction = health / 100;
  let expectedRemainingDays = Math.round(baseLifetimeYears * 365 * remainingLifeFraction);

  if (componentId.indexOf('fan') !== -1 || componentId.indexOf('motor') !== -1 || componentId.indexOf('drive') !== -1) {
    expectedRemainingDays = Math.round(expectedRemainingDays * 0.7);
    failureMode = 'Mechanical wear (bearings, motor)';
  } else if (componentId.indexOf('hpa') !== -1 || componentId.indexOf('sspa') !== -1) {
    failureMode = 'Thermal degradation of power transistors';
  } else if (componentId.indexOf('lna') !== -1) {
    failureMode = 'Gradual noise figure degradation';
  } else if (componentId.indexOf('modem') !== -1) {
    failureMode = 'Digital component aging';
  } else if (componentId.indexOf('beacon') !== -1 || componentId.indexOf('receiver') !== -1) {
    failureMode = 'RF component drift';
  } else if (componentId.indexOf('processor') !== -1 || componentId.indexOf('tracking') !== -1) {
    failureMode = 'Electronic component aging';
  }

  if (health < 40 || expectedRemainingDays < 60) {
    riskLevel = 'CRITICAL';
    daysUntilFailure = Math.max(30, Math.min(expectedRemainingDays, 90));
    maintenanceWindow = 'Within 30 days';
    recommendedAction = 'Schedule immediate replacement. Order spare parts if not in stock.';
  } else if (health < 60 || expectedRemainingDays < 180) {
    riskLevel = 'HIGH';
    daysUntilFailure = Math.max(60, Math.min(expectedRemainingDays, 180));
    maintenanceWindow = 'Within 2-3 months';
    recommendedAction = 'Schedule preventive maintenance. Verify spare parts availability.';
  } else if (health < 80 || expectedRemainingDays < 365) {
    riskLevel = 'MEDIUM';
    daysUntilFailure = Math.max(180, Math.min(expectedRemainingDays, 365));
    maintenanceWindow = 'Within 6 months';
    recommendedAction = 'Add to next scheduled maintenance window.';
  } else {
    riskLevel = 'LOW';
    daysUntilFailure = expectedRemainingDays > 730 ? null : expectedRemainingDays;
    maintenanceWindow = 'Standard schedule';
    recommendedAction = 'Continue routine monitoring. No immediate action required.';
  }

  if (daysUntilFailure && daysUntilFailure < 730) predictedDate = isoDate(addDaysMs(nowMs, daysUntilFailure));

  const mtbfYears = Math.round(mtbfData.mtbfHours / 8760);
  justification = componentName + ' is currently at ' + health + '% health. ';
  if (health >= 90) {
    justification += 'All readings are within normal parameters. ';
    justification += 'Based on industry MTBF of ' + mtbfYears + ' years for ' + (mtbfData ? mtbfData.category : category) + ' components, ';
    justification += 'no failure is predicted within the next 2 years with standard maintenance.';
  } else if (health >= 70) {
    justification += 'Some minor degradation detected. ';
    if (failureMode) justification += 'Primary risk: ' + failureMode + '. ';
    justification += 'Based on ' + mtbfYears + '-year MTBF and current degradation rate, ';
    justification += 'recommend inspection within ' + Math.round((daysUntilFailure as number) / 30) + ' months.';
  } else {
    justification += 'Significant degradation detected requiring attention. ';
    if (failureMode) justification += 'Likely failure mode: ' + failureMode + '. ';
    justification +=
      'Current health indicates approximately ' + Math.round(remainingLifeFraction * 100) + '% of expected service life remaining.';
  }

  if (componentId.indexOf('fan') !== -1) {
    if (health < 90) leadIndicators.push({ sensor: 'RPM', concern: 'Decreasing rotation speed indicates bearing wear' });
    if (health < 80) leadIndicators.push({ sensor: 'Temperature', concern: 'Rising temperature due to reduced airflow' });
  } else if (componentId.indexOf('hpa') !== -1 || componentId.indexOf('sspa') !== -1) {
    if (health < 90) leadIndicators.push({ sensor: 'Efficiency', concern: 'Declining power efficiency' });
    if (health < 80) leadIndicators.push({ sensor: 'Temperature', concern: 'Elevated operating temperature' });
  } else if (componentId.indexOf('motor') !== -1 || componentId.indexOf('drive') !== -1) {
    if (health < 90) leadIndicators.push({ sensor: 'Current', concern: 'Increased motor current draw' });
    if (health < 80) leadIndicators.push({ sensor: 'Position Error', concern: 'Tracking accuracy degradation' });
  }

  return {
    componentId: component.id,
    componentName,
    predictedFailureDate: predictedDate,
    daysUntilFailure,
    confidence: health >= 80 ? 75 : health >= 60 ? 70 : 65,
    riskLevel,
    healthScore: health,
    aiHealth: health,
    failureMode,
    justification,
    leadIndicators,
    recommendedAction,
    maintenanceWindow,
    inventoryAlert: riskLevel === 'CRITICAL' || riskLevel === 'HIGH' ? 'Verify spare parts availability' : null,
    dependencyRisk: null,
    trend: { direction: health < 80 ? 'declining' : 'stable', anomalousReadings: health < 70 ? 1 : 0 },
    isAIGenerated: false,
    generatedAt: new Date(nowMs).toISOString(),
  };
}

export function createAiPredictionService(deps: AiPredictionDeps): AiPredictionService {
  const timeoutMs = deps.timeoutMs ?? AI_PREDICTION_LOCAL_TIMEOUT_MS;
  const schedule = deps.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  const cancel = deps.clearTimeout ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));

  function collect(component: PredictionComponent, options: GenerateOptions = {}): AnalysisData {
    return collectAnalysisData(component, options, deps.lifecycle, deps.inventory);
  }

  function generatePrediction(component: PredictionComponent, options: GenerateOptions = {}): Promise<AiPrediction> {
    return new Promise((resolve) => {
      const analysisData = collect(component, options);
      if (analysisData.sensorReadings.length === 0) {
        resolve(generateFallbackPrediction(component, deps.nowMs()));
        return;
      }
      const prompt = buildPredictionPrompt(analysisData);
      let settled = false;
      const finish = (prediction: AiPrediction): void => {
        if (settled) return;
        settled = true;
        resolve(prediction);
      };
      const timer = schedule(() => finish(generateFallbackPrediction(component, deps.nowMs())), timeoutMs);
      deps.transport
        .call('predict', buildAiMessages(AI_PREDICTION_SYSTEM_PROMPT, prompt))
        .then((response) => {
          cancel(timer);
          finish(parseAIPrediction(response, analysisData, deps.nowMs()));
        })
        .catch(() => {
          cancel(timer);
          finish(generateFallbackPrediction(component, deps.nowMs()));
        });
    });
  }

  function generateBatchPredictions(
    components: PredictionComponent[],
    options: GenerateOptions = {},
    onProgress?: BatchProgress,
  ): Promise<Record<string, AiPrediction>> {
    return new Promise((resolve) => {
      const predictions: Record<string, AiPrediction> = {};
      const total = components.length;
      if (total === 0) {
        resolve(predictions);
        return;
      }
      const queue = components.slice();
      let completed = 0;
      let active = 0;

      const processNext = (): void => {
        while (active < AI_PREDICTION_BATCH_CONCURRENCY && queue.length > 0) {
          active++;
          const comp = queue.shift() as PredictionComponent;
          generatePrediction(comp, options).then((prediction) => {
            predictions[comp.id] = prediction;
            completed++;
            active--;
            onProgress?.(comp.id, prediction, completed, total);
            if (completed >= total) resolve(predictions);
            else processNext();
          });
        }
      };
      processNext();
    });
  }

  return {
    generatePrediction,
    generateBatchPredictions,
    collectAnalysisData: collect,
    getLifecycleKnowledge: (equipmentType) =>
      deps.lifecycle && deps.lifecycle.equipmentTypes ? deps.lifecycle.equipmentTypes[equipmentType] || null : null,
    getAllDependencies: () => (deps.lifecycle && deps.lifecycle.componentDependencies ? deps.lifecycle.componentDependencies : {}),
  };
}
